// App.js
import React, { Component } from 'react';
import { HashRouter as Router, Switch, Route, Redirect, withRouter } from 'react-router-dom';

import './App.css';

import Login from './components/Login/Login';
import VerifyForm from './containers/Login/VerifyForm';
import ChangePassword from './containers/Login/ChangePassword';
import TempPassword from './containers/Login/tempPassword';
import Sign_up from './components/Login/Login';

import Main from './containers/Main/Main';
import PrivateRoute from './components/ProtectedRoute/PrivateRoute';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import 'react-toastify/dist/ReactToastify.css';

import Prices from './containers/Payment/Prices';
import Account from './containers/Payment/Account';

import Portfolio from './containers/Portfolio/PortfolioOverview';
import Trader_form from './containers/Trader_Form/Trader_Form';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Scanner_form from './containers/Scanners/scanner_form';
import Order_history from './containers/Order_history/Order_history';
import About from './containers/About/About';

import Landing from './containers/Landing_Page/Landing';
import Terms from './components/Terms/Terms';
import Privacy from './components/Privacy/Privacy';

import { persistor, Store } from './redux/Store';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { Provider } from 'react-redux';

import jwt_decode from 'jwt-decode';
import jwtAuthService from './services/jwtAuthService';

import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

import { OnboardingProvider } from './components/Onboarding/OnboardingProvider';

import { GoogleOAuthProvider } from '@react-oauth/google';

// Matte/steel-blue design system — identical to the Portfolio page tokens.
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#4d8fc7' },
    secondary: { main: '#3f6fb0' },
    success: { main: '#46b88a' },
    error: { main: '#d97b72' },
    warning: { main: '#d9a441' },
    info: { main: '#4d8fc7' },
    background: { default: '#0a0c11', paper: '#0f1218' },
    divider: 'rgba(255,255,255,.06)',
    text: { primary: '#e8edf7', secondary: '#9aa6bd' },
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: '#0a0c11', color: '#e8edf7' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0b0e15', borderRadius: 9,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d8fc7', borderWidth: 1 },
        },
        input: { color: '#f2f6ff' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#7b88a2', '&.Mui-focused': { color: '#4d8fc7' } } } },
    MuiPaper: { styleOverrides: { outlined: { borderColor: 'rgba(255,255,255,.06)' } } },
    MuiSlider: { styleOverrides: { root: { color: '#4d8fc7' } } },
  },
});

const CLOCK_SKEW_MS = 30 * 1000;

// Helper: check if token is expired (with grace period for clock skew)
const isTokenExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) return true;
  try {
    const decoded = jwt_decode(token);
    if (!decoded.exp) return false;
    return decoded.exp * 1000 < Date.now() - CLOCK_SKEW_MS;
  } catch (e) {
    return true;
  }
};

const isAuthenticated = () => {
  return localStorage.getItem('authenticated') === 'true' && !isTokenExpired();
};

// Debounced activity listener — refresh at most once every 30s
let lastRefresh = 0;
const REFRESH_THROTTLE_MS = 30 * 1000;

const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
activityEvents.forEach(event => {
  window.addEventListener(event, () => {
    const now = Date.now();
    if (now - lastRefresh > REFRESH_THROTTLE_MS) {
      lastRefresh = now;
      jwtAuthService.refreshSession();
    }
  }, { passive: true });
});

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// SessionWatcher: forces redirect to /sign-in when session expires
// SessionWatcher: forces redirect to /sign-in when session expires
class SessionWatcherInner extends Component {
  constructor(props) {
    super(props);
    this.state = { expiredAt: 0 };
  }

  componentDidMount() {
    this.interval = setInterval(this.checkSession, 15000);
    // Don't run checkSession immediately on mount — give the app a chance
    // to hydrate redux-persist and finish login flows
    this.mountTime = Date.now();

    window.addEventListener('focus', this.checkSession);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('session-expired', this.handleExpiredEvent);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener('focus', this.checkSession);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('session-expired', this.handleExpiredEvent);
  }

  componentDidUpdate(_, prevState) {
    if (this.state.expiredAt && this.state.expiredAt !== prevState.expiredAt) {
      setTimeout(() => this.setState({ expiredAt: 0 }), 0);
    }
  }

  handleVisibility = () => {
    if (document.visibilityState === 'visible') this.checkSession();
  };

  handleStorageChange = (e) => {
    // Only trigger if the token is explicitly removed by another tab
    if (e.key === 'token' && e.oldValue && !e.newValue) {
      this.triggerExpiry();
    }
  };

  handleExpiredEvent = () => {
    this.triggerExpiry();
  };

  checkSession = () => {
    // Grace period: don't fire during the first 5 seconds after mount
    // to let login/hydration finish
    if (Date.now() - this.mountTime < 5000) return;

    const isAuth = localStorage.getItem('authenticated') === 'true';
    if (!isAuth) return;

    // ONLY log out on hard signals:
    // 1. The JWT itself has actually expired (server-issued exp claim)
    // We do NOT check isSessionActive() here because session_start may not
    // be set if the login flow doesn't use jwtAuthService.setSession()
    if (isTokenExpired()) {
      jwtAuthService.logout();
      this.triggerExpiry();
    }
  };

  triggerExpiry = () => {
    this.setState({ expiredAt: Date.now() });
  };

  render() {
    const { location } = this.props;
    const onProtectedRoute = location.pathname.startsWith('/profile');
    if (this.state.expiredAt && onProtectedRoute) {
      return <Redirect to="/sign-in" />;
    }
    return null;
  }
}

const SessionWatcher = withRouter(SessionWatcherInner);

const AuthRoute = ({ component: Component, ...props }) => (
  <Route
    {...props}
    render={(routeProps) => {
      // If they have an expired token sitting around, clean it up
      if (localStorage.getItem('authenticated') === 'true' && isTokenExpired()) {
        jwtAuthService.logout();
      }

      return isAuthenticated() ? (
        <Redirect to="/profile/portfolio" />
      ) : (
        <Component {...routeProps} />
      );
    }}
  />
);

class App extends Component {
  render() {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Provider store={Store}>
          <PersistGate persistor={persistor}>
            <Router basename="/">
              <OnboardingProvider>
              <SessionWatcher />

              <Switch>
                <AuthRoute exact path="/" component={Landing} />
                <AuthRoute exact path="/features" component={Landing} />
                <AuthRoute exact path="/about" component={Landing} />
                <AuthRoute exact path="/services" component={Landing} />
                <AuthRoute exact path="/team" component={Landing} />
                <AuthRoute exact path="/contact" component={Landing} />

                <AuthRoute exact path="/sign-in" component={Login} />
                <AuthRoute exact path="/verify" component={VerifyForm} />
                <AuthRoute exact path="/sign-up" component={Sign_up} />
                <AuthRoute exact path="/changePassword" component={ChangePassword} />
                <AuthRoute exact path="/tempPassword" component={TempPassword} />

                <Route path="/profile">
                  <Main>
                    <RouteErrorBoundary>
                    <Switch>
                      <PrivateRoute path="/profile/portfolio">
                        <Portfolio />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/traderform">
                        <Trader_form />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/scanners">
                        <Scanner_form />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/orderhistory">
                        <Order_history />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/about">
                        <About />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/terms">
                        <Terms />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/privacy">
                        <Privacy />
                      </PrivateRoute>
                      <Elements stripe={stripePromise}>
                        <PrivateRoute path="/profile/subscription">
                          <Prices />
                        </PrivateRoute>
                        <PrivateRoute path="/profile/account">
                          <Account />
                        </PrivateRoute>
                      </Elements>
                    </Switch>
                    </RouteErrorBoundary>
                  </Main>
                </Route>
              </Switch>
              </OnboardingProvider>
            </Router>
          </PersistGate>
        </Provider>
      </ThemeProvider>
      </GoogleOAuthProvider>
    );
  }
}

export default App;